package com.nonopn.skywatcher.common.api

import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.websocket.DefaultClientWebSocketSession
import io.ktor.client.plugins.websocket.ws
import io.ktor.client.plugins.websocket.wss
import io.ktor.client.request.bearerAuth
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.http.ContentType
import io.ktor.http.contentType

@Suppress("SwallowedException", "MagicNumber")
suspend inline fun <reified O> manageAnswer(request: HttpResponse): O {
    return request.status.value.let {
        if (it in 200..299) {
            return@let request.body()
        }

        val errorFromBackend: String = request.body()
        throw BackendErrorException(
            "Error",
            errorFromBackend,
            request.status
        )
    }
}

data class DoNetworkCalls(
    val client: HttpClient
) {
    suspend fun webSocket(
        url: String,
        block: suspend DefaultClientWebSocketSession.() -> Unit
    ) {
        client.ws(urlString = url.replace("https://", "wss://"), block = block)
    }

    suspend inline fun <reified T> get(
        url: String
    ): T = manageAnswer(client.get(url))

    suspend inline fun <reified O, T> get(
        url: String,
        mapResult: (result: O) -> T
    ): T {
        val result: O = manageAnswer(client.get(url))

        return mapResult(result)
    }

    suspend inline fun <reified O, T> post(
        url: String,
        mapResult: (result: O) -> T
    ): T {
        val result: O = manageAnswer(client.post(url))

        return mapResult(result)
    }

    suspend inline fun <reified T> delete(
        url: String
    ): T = manageAnswer(client.delete(url))

    suspend inline fun <reified I, reified O> delete(
        url: String,
        body: I
    ): O {
        val request = client.delete(url) {
            contentType(ContentType.Application.Json)
            setBody(body)
        }

        return manageAnswer(request)
    }

    suspend inline fun <reified T> post(
        url: String
    ): T = manageAnswer(client.post(url))

    suspend inline fun <reified T> put(
        url: String
    ): T = manageAnswer(client.put(url))

    suspend inline fun <reified I, reified O, T> post(
        url: String,
        body: I,
        mapResult: (result: O) -> T
    ): T {
        val request = client.post(url) {
            contentType(ContentType.Application.Json)
            setBody(body)
        }

        val result: O = manageAnswer(request)

        return mapResult(result)
    }

    suspend inline fun <reified I, reified O, T> put(
        url: String,
        body: I,
        mapResult: (result: O) -> T
    ): T {
        val request = client.put(url) {
            contentType(ContentType.Application.Json)
            setBody(body)
        }

        val result: O = manageAnswer(request)

        return mapResult(result)
    }

    suspend inline fun <reified I, reified O> post(
        url: String,
        body: I
    ): O {
        val request = client.post(url) {
            contentType(ContentType.Application.Json)
            setBody(body)
        }

        return manageAnswer(request)
    }

    suspend inline fun <reified I, reified O> put(
        url: String,
        body: I
    ): O {
        val request = client.put(url) {
            contentType(ContentType.Application.Json)
            setBody(body)
        }

        return manageAnswer(request)
    }

    suspend inline fun <reified I, reified O> post(
        url: String,
        optionalBearer: String? = null,
        body: I
    ): O {
        val request = client.post(url) {
            if (null != optionalBearer) {
                // option to override the bearer for this client
                bearerAuth(optionalBearer)
            }

            contentType(ContentType.Application.Json)
            setBody(body)
        }

        return manageAnswer(request)
    }
}
